// ══════════════════════════════════════════════════════
// ROLE MODULE HUBS — Cards for each role's landing page
// ══════════════════════════════════════════════════════
export interface RoleModuleCardData {
  id: string;
  icon: string;
  title: string;
  description: string;
  path: string;
  badge?: { text: string; color: "red" | "amber" | "emerald" | "sky" | "purple" | "primary" };
  color: "emerald" | "primary" | "purple" | "amber" | "red" | "sky" | "teal" | "foreground" | "orange";
}

export interface RoleHubConfig {
  roleId: string;
  title: string;
  subtitle: string;
  gradient: string;
  modules: RoleModuleCardData[];
}

// ── Color utility maps ──
export const CARD_COLORS: Record<RoleModuleCardData["color"], { iconBg: string; iconText: string; accentBar: string; hoverShadow: string }> = {
  emerald: { iconBg: "bg-emerald-100", iconText: "text-emerald-600", accentBar: "bg-emerald-400", hoverShadow: "hover:shadow-glow-emerald" },
  primary: { iconBg: "bg-primary-100", iconText: "text-primary-600", accentBar: "bg-primary-400", hoverShadow: "hover:shadow-glow-primary" },
  purple: { iconBg: "bg-purple-100", iconText: "text-purple-600", accentBar: "bg-purple-400", hoverShadow: "hover:shadow-glow-purple" },
  amber: { iconBg: "bg-amber-100", iconText: "text-amber-600", accentBar: "bg-amber-400", hoverShadow: "hover:shadow-glow-amber" },
  red: { iconBg: "bg-red-100", iconText: "text-red-600", accentBar: "bg-red-400", hoverShadow: "hover:shadow-glow-red" },
  sky: { iconBg: "bg-sky-100", iconText: "text-sky-600", accentBar: "bg-sky-400", hoverShadow: "hover:shadow-glow-primary" },
  teal: { iconBg: "bg-teal-100", iconText: "text-teal-600", accentBar: "bg-teal-400", hoverShadow: "hover:shadow-glow-accent" },
  foreground: { iconBg: "bg-foreground-100", iconText: "text-foreground-600", accentBar: "bg-foreground-400", hoverShadow: "" },
  orange: { iconBg: "bg-orange-100", iconText: "text-orange-600", accentBar: "bg-orange-400", hoverShadow: "hover:shadow-glow-amber" },
};

export const BADGE_COLORS: Record<string, string> = {
  red: "bg-red-100 text-red-700 border-red-200",
  amber: "bg-amber-100 text-amber-700 border-amber-200",
  emerald: "bg-emerald-100 text-emerald-700 border-emerald-200",
  sky: "bg-sky-100 text-sky-700 border-sky-200",
  purple: "bg-purple-100 text-purple-700 border-purple-200",
  primary: "bg-primary-100 text-primary-700 border-primary-200",
};

// ══════════════════════════════════════════════════════
// DIRECTOR
// ══════════════════════════════════════════════════════
export const DIRECTOR_MODULES: RoleModuleCardData[] = [
  {
    id: "dir-convivencia",
    icon: "ri-shield-user-line",
    title: "Gestión de Convivencia Escolar",
    description: "Revisa casos activos, alertas, seguimientos y desempeño del encargado de convivencia.",
    path: "/convivencia",
    color: "primary",
  },
  {
    id: "dir-orientacion",
    icon: "ri-compass-3-line",
    title: "Gestión de Orientación",
    description: "Monitorea casos judicializados, informes pendientes y seguimiento de orientación.",
    path: "/orientacion",
    color: "purple",
  },
  {
    id: "dir-psicologo",
    icon: "ri-psychotherapy-line",
    title: "Gestión Psicólogo",
    description: "Supervisa seguimiento de casos psicológicos y carga de intervenciones.",
    path: "/psicologo",
    color: "teal",
  },
  {
    id: "dir-judicial",
    icon: "ri-scales-3-line",
    title: "Requerimientos Judiciales",
    description: "Visualiza solicitudes de tribunal, plazos críticos e informes enviados.",
    path: "/judicial",
    color: "amber",
    badge: { text: "Crítico", color: "red" },
  },
  {
    id: "dir-reportes",
    icon: "ri-file-chart-line",
    title: "Reportes de Gestión",
    description: "Descarga reportes por área, encargado, estado de casos y cumplimiento.",
    path: "/director/reportes/consolidado",
    color: "foreground",
  },
  {
    id: "dir-indicadores",
    icon: "ri-bar-chart-2-line",
    title: "Indicadores Institucionales",
    description: "Observa métricas globales de casos, cumplimiento, tiempos de respuesta y alertas.",
    path: "/director/indicadores",
    color: "sky",
  },
  {
    id: "dir-ia",
    icon: "ri-robot-2-line",
    title: "Asistente IA",
    description: "Consulta protocolos, genera resúmenes e informes asistidos por IA.",
    path: "/ia/consulta",
    color: "emerald",
    badge: { text: "IA", color: "emerald" },
  },
  {
    id: "dir-admin",
    icon: "ri-settings-3-line",
    title: "Administración",
    description: "Gestiona usuarios, permisos, documentos institucionales y auditoría.",
    path: "/admin/usuarios",
    color: "foreground",
  },
];

// ══════════════════════════════════════════════════════
// ENCARGADO DE CONVIVENCIA ESCOLAR
// ══════════════════════════════════════════════════════
export const CONVIVENCIA_MODULES: RoleModuleCardData[] = [
  {
    id: "conv-casos",
    icon: "ri-folder-3-line",
    title: "Casos de Convivencia",
    description: "Revisa, crea y da seguimiento a casos activos de convivencia escolar.",
    path: "/convivencia/casos",
    color: "primary",
    badge: { text: "6 activos", color: "primary" },
  },
  {
    id: "conv-nuevo",
    icon: "ri-add-circle-line",
    title: "Crear Nuevo Caso",
    description: "Registra un incidente o inicia un nuevo caso de convivencia escolar.",
    path: "/convivencia/casos/nuevo",
    color: "primary",
  },
  {
    id: "conv-alertas",
    icon: "ri-alert-line",
    title: "Alertas de Convivencia",
    description: "Visualiza casos críticos, pendientes y situaciones que requieren atención inmediata.",
    path: "/convivencia/alertas",
    color: "red",
    badge: { text: "3", color: "red" },
  },
  {
    id: "conv-seguimientos",
    icon: "ri-line-chart-line",
    title: "Seguimientos",
    description: "Revisa compromisos, entrevistas, acuerdos y acciones en desarrollo.",
    path: "/convivencia/seguimientos",
    color: "amber",
  },
  {
    id: "conv-entrevistas",
    icon: "ri-mic-line",
    title: "Entrevistas y Actas",
    description: "Registra entrevistas con estudiantes, apoderados y docentes.",
    path: "/convivencia/entrevistas",
    color: "emerald",
  },
  {
    id: "conv-informes",
    icon: "ri-file-chart-line",
    title: "Informes de Convivencia",
    description: "Genera y consulta informes relacionados con convivencia escolar.",
    path: "/convivencia/informes",
    color: "purple",
  },
  {
    id: "conv-expedientes",
    icon: "ri-user-search-line",
    title: "Expedientes",
    description: "Accede a los expedientes de estudiantes vinculados a casos de convivencia.",
    path: "/convivencia/expedientes",
    color: "sky",
  },
  {
    id: "conv-ia",
    icon: "ri-robot-2-line",
    title: "Protocolos e IA",
    description: "Consulta protocolos, normativa y apoyo de IA para actuación institucional.",
    path: "/ia/consulta",
    color: "emerald",
    badge: { text: "IA", color: "emerald" },
  },
];

// ══════════════════════════════════════════════════════
// ORIENTADOR/A
// ══════════════════════════════════════════════════════
export const ORIENTADOR_MODULES: RoleModuleCardData[] = [
  {
    id: "ori-casos",
    icon: "ri-folder-3-line",
    title: "Casos de Orientación",
    description: "Gestiona casos de orientación activos y realiza seguimiento institucional.",
    path: "/orientacion/casos",
    color: "purple",
    badge: { text: "5 activos", color: "purple" },
  },
  {
    id: "ori-judicializados",
    icon: "ri-scales-3-line",
    title: "Casos Judicializados",
    description: "Revisa estudiantes con requerimientos judiciales o seguimiento por tribunal.",
    path: "/orientacion/judicializados",
    color: "red",
    badge: { text: "2", color: "red" },
  },
  {
    id: "ori-insumos",
    icon: "ri-send-plane-line",
    title: "Solicitud de Insumos Docentes",
    description: "Solicita antecedentes a profesores para elaborar informes.",
    path: "/orientacion/insumos",
    color: "amber",
    badge: { text: "3 pend.", color: "amber" },
  },
  {
    id: "ori-insumos-recibidos",
    icon: "ri-inbox-line",
    title: "Insumos Recibidos",
    description: "Revisa respuestas enviadas por docentes y valida su utilidad.",
    path: "/orientacion/insumos-recibidos",
    color: "emerald",
  },
  {
    id: "ori-informes",
    icon: "ri-file-warning-line",
    title: "Informes para Tribunal",
    description: "Elabora, revisa y gestiona informes requeridos por el tribunal.",
    path: "/orientacion/informes",
    color: "amber",
    badge: { text: "1 pend.", color: "amber" },
  },
  {
    id: "ori-ia-informe",
    icon: "ri-magic-line",
    title: "Generador de Informe IA",
    description: "Usa IA para organizar antecedentes y generar borradores de informe.",
    path: "/ia/informe",
    color: "emerald",
    badge: { text: "IA", color: "emerald" },
  },
  {
    id: "ori-alertas",
    icon: "ri-alert-line",
    title: "Alertas y Plazos",
    description: "Visualiza informes próximos a vencer, insumos faltantes y pendientes críticos.",
    path: "/orientacion/alertas",
    color: "red",
  },
  {
    id: "ori-expedientes",
    icon: "ri-user-search-line",
    title: "Expedientes",
    description: "Consulta expedientes y antecedentes del estudiante vinculados a orientación.",
    path: "/orientacion/expedientes",
    color: "sky",
  },
];

// ══════════════════════════════════════════════════════
// PSICÓLOGO/A
// ══════════════════════════════════════════════════════
export const PSICOLOGO_MODULES: RoleModuleCardData[] = [
  {
    id: "psi-casos",
    icon: "ri-folder-3-line",
    title: "Casos Psicológicos",
    description: "Revisa y gestiona casos asignados al área psicológica.",
    path: "/psicologo/casos",
    color: "teal",
    badge: { text: "4 activos", color: "emerald" },
  },
  {
    id: "psi-intervenciones",
    icon: "ri-heart-pulse-line",
    title: "Intervenciones Psicológicas",
    description: "Registra acciones, entrevistas y seguimiento psicológico.",
    path: "/psicologo/intervenciones",
    color: "teal",
  },
  {
    id: "psi-seguimiento",
    icon: "ri-user-heart-line",
    title: "Seguimiento Individual",
    description: "Monitorea evolución, acciones pendientes y continuidad de apoyo.",
    path: "/psicologo/seguimiento",
    color: "emerald",
  },
  {
    id: "psi-derivaciones",
    icon: "ri-share-forward-line",
    title: "Derivaciones",
    description: "Gestiona derivaciones internas y externas.",
    path: "/psicologo/derivaciones-externas",
    color: "purple",
  },
  {
    id: "psi-confidencial",
    icon: "ri-lock-line",
    title: "Registro Confidencial",
    description: "Accede a antecedentes sensibles resguardados por permisos especiales.",
    path: "/psicologo/confidencial",
    color: "red",
    badge: { text: "Privado", color: "red" },
  },
  {
    id: "psi-informes",
    icon: "ri-file-text-line",
    title: "Informes Psicoeducativos",
    description: "Elabora o consulta informes del área psicológica.",
    path: "/psicologo/informes",
    color: "amber",
  },
  {
    id: "psi-alertas",
    icon: "ri-alert-line",
    title: "Alertas Psicológicas",
    description: "Visualiza casos sin seguimiento, pendientes o de alta prioridad.",
    path: "/psicologo/alertas",
    color: "red",
  },
  {
    id: "psi-expedientes",
    icon: "ri-user-search-line",
    title: "Expedientes",
    description: "Consulta expedientes de estudiantes asignados a seguimiento psicológico.",
    path: "/psicologo/expedientes",
    color: "sky",
  },
];

// ══════════════════════════════════════════════════════
// INSPECTOR GENERAL
// ══════════════════════════════════════════════════════
export const INSPECTOR_MODULES: RoleModuleCardData[] = [
  {
    id: "ins-registrar",
    icon: "ri-add-circle-line",
    title: "Registrar Incidente",
    description: "Crea registros iniciales de hechos ocurridos en el establecimiento.",
    path: "/convivencia/casos/nuevo",
    color: "primary",
  },
  {
    id: "ins-casos",
    icon: "ri-folder-3-line",
    title: "Casos Asignados",
    description: "Visualiza casos en los que debes intervenir o hacer seguimiento.",
    path: "/convivencia/casos",
    color: "primary",
    badge: { text: "Asignados", color: "primary" },
  },
  {
    id: "ins-evidencias",
    icon: "ri-image-add-line",
    title: "Evidencias",
    description: "Adjunta antecedentes, registros y documentos relevantes.",
    path: "/intervenciones/evidencias",
    color: "purple",
  },
  {
    id: "ins-alertas",
    icon: "ri-alert-line",
    title: "Alertas",
    description: "Revisa pendientes críticos o acciones por completar.",
    path: "/convivencia/alertas",
    color: "red",
    badge: { text: "2", color: "red" },
  },
  {
    id: "ins-protocolos",
    icon: "ri-book-open-line",
    title: "Protocolos de Actuación",
    description: "Consulta el reglamento y protocolos institucionales.",
    path: "/ia/protocolos",
    color: "emerald",
  },
  {
    id: "ins-seguimientos",
    icon: "ri-line-chart-line",
    title: "Seguimientos",
    description: "Revisa acciones pendientes vinculadas a casos activos.",
    path: "/convivencia/seguimientos",
    color: "amber",
  },
];

// ══════════════════════════════════════════════════════
// PROFESOR JEFE
// ══════════════════════════════════════════════════════
export const PROFESOR_JEFE_MODULES: RoleModuleCardData[] = [
  {
    id: "pj-solicitudes",
    icon: "ri-inbox-line",
    title: "Solicitudes de Informe",
    description: "Responde solicitudes de orientación, convivencia o dirección.",
    path: "/profesor-jefe/solicitudes",
    color: "amber",
    badge: { text: "3 pend.", color: "amber" },
  },
  {
    id: "pj-formularios",
    icon: "ri-file-text-line",
    title: "Formularios Pendientes",
    description: "Completa antecedentes de estudiantes solicitados por el colegio.",
    path: "/profesor-jefe/formularios",
    color: "primary",
  },
  {
    id: "pj-historial",
    icon: "ri-history-line",
    title: "Informes Enviados",
    description: "Revisa solicitudes ya respondidas y su estado.",
    path: "/profesor-jefe/historial",
    color: "emerald",
  },
  {
    id: "pj-devoluciones",
    icon: "ri-arrow-go-back-line",
    title: "Solicitudes Devueltas",
    description: "Corrige formularios o informes que requieren ajustes.",
    path: "/profesor-jefe/solicitudes",
    color: "red",
  },
  {
    id: "pj-protocolos",
    icon: "ri-book-open-line",
    title: "Protocolos Autorizados",
    description: "Consulta orientaciones institucionales, si el rol lo permite.",
    path: "/ia/consulta",
    color: "purple",
  },
];

// ══════════════════════════════════════════════════════
// DOCENTE
// ══════════════════════════════════════════════════════
export const DOCENTE_MODULES: RoleModuleCardData[] = [
  {
    id: "doc-pendientes",
    icon: "ri-inbox-line",
    title: "Formularios Pendientes",
    description: "Completa solicitudes de antecedentes o insumos docentes.",
    path: "/docente/solicitudes",
    color: "amber",
    badge: { text: "Pendiente", color: "amber" },
  },
  {
    id: "doc-desarrollo",
    icon: "ri-pen-nib-line",
    title: "Informes en Desarrollo",
    description: "Continúa la redacción de informes aún no enviados.",
    path: "/docente/formularios",
    color: "primary",
  },
  {
    id: "doc-enviados",
    icon: "ri-check-double-line",
    title: "Informes Enviados",
    description: "Consulta el estado de tus respuestas e informes entregados.",
    path: "/docente/historial",
    color: "emerald",
  },
  {
    id: "doc-devueltas",
    icon: "ri-arrow-go-back-line",
    title: "Solicitudes Devueltas",
    description: "Corrige informes que requieren más objetividad o precisión.",
    path: "/docente/devoluciones",
    color: "red",
  },
  {
    id: "doc-ia",
    icon: "ri-robot-2-line",
    title: "Asistente IA de Redacción",
    description: "Mejora redacción, objetividad y claridad de tus informes.",
    path: "/ia/consulta",
    color: "emerald",
    badge: { text: "IA", color: "emerald" },
  },
];

// ══════════════════════════════════════════════════════
// ADMINISTRADOR DEL SISTEMA
// ══════════════════════════════════════════════════════
export const ADMIN_MODULES: RoleModuleCardData[] = [
  {
    id: "adm-usuarios",
    icon: "ri-team-line",
    title: "Usuarios y Roles",
    description: "Administra usuarios, perfiles y permisos del sistema.",
    path: "/admin/usuarios",
    color: "primary",
  },
  {
    id: "adm-documentos",
    icon: "ri-file-text-line",
    title: "Documentos Institucionales",
    description: "Carga reglamentos, protocolos y documentos base.",
    path: "/admin/documentos",
    color: "purple",
  },
  {
    id: "adm-config",
    icon: "ri-settings-4-line",
    title: "Configuración General",
    description: "Ajusta parámetros, módulos y opciones del sistema.",
    path: "/admin/configuracion",
    color: "amber",
  },
  {
    id: "adm-auditoria",
    icon: "ri-shield-check-line",
    title: "Auditoría",
    description: "Revisa actividad, trazabilidad y accesos del sistema.",
    path: "/admin/auditoria",
    color: "red",
  },
  {
    id: "adm-permisos",
    icon: "ri-shield-keyhole-line",
    title: "Seguridad y Permisos",
    description: "Configura niveles de acceso y visibilidad por rol.",
    path: "/admin/permisos",
    color: "foreground",
  },
];

// ══════════════════════════════════════════════════════
// GETTER — returns hub config for a role
// ══════════════════════════════════════════════════════
export function getRoleHubConfig(roleId: string): RoleHubConfig {
  switch (roleId) {
    case "director":
      return {
        roleId: "director",
        title: "Resumen Ejecutivo Director",
        subtitle: "¿Qué puedes gestionar hoy?",
        gradient: "from-foreground-800 to-foreground-900",
        modules: DIRECTOR_MODULES,
      };
    case "convivencia":
      return {
        roleId: "convivencia",
        title: "Gestión de Convivencia Escolar",
        subtitle: "Tus accesos principales",
        gradient: "from-primary-600 via-primary-700 to-primary-800",
        modules: CONVIVENCIA_MODULES,
      };
    case "orientador":
      return {
        roleId: "orientador",
        title: "Gestión de Orientación",
        subtitle: "Tus herramientas disponibles",
        gradient: "from-purple-600 via-purple-700 to-amber-700",
        modules: ORIENTADOR_MODULES,
      };
    case "psicologo":
      return {
        roleId: "psicologo",
        title: "Gestión Psicológica",
        subtitle: "Tus módulos principales",
        gradient: "from-teal-600 via-teal-700 to-emerald-700",
        modules: PSICOLOGO_MODULES,
      };
    case "inspector":
      return {
        roleId: "inspector",
        title: "Gestión de Inspectoría",
        subtitle: "Accesos principales",
        gradient: "from-primary-600 via-primary-700 to-red-700",
        modules: INSPECTOR_MODULES,
      };
    case "profesor_jefe":
      return {
        roleId: "profesor_jefe",
        title: "Gestión del Profesor Jefe",
        subtitle: "Tus accesos rápidos",
        gradient: "from-primary-600 via-primary-700 to-sky-700",
        modules: PROFESOR_JEFE_MODULES,
      };
    case "docente":
      return {
        roleId: "docente",
        title: "Mis Solicitudes",
        subtitle: "Lo que puedes hacer hoy",
        gradient: "from-emerald-500 via-emerald-600 to-teal-600",
        modules: DOCENTE_MODULES,
      };
    case "admin":
      return {
        roleId: "admin",
        title: "Administración del Sistema",
        subtitle: "Herramientas disponibles",
        gradient: "from-foreground-700 via-foreground-800 to-foreground-900",
        modules: ADMIN_MODULES,
      };
    default:
      return {
        roleId: "convivencia",
        title: "Gestión de Convivencia Escolar",
        subtitle: "Tus accesos principales",
        gradient: "from-primary-600 via-primary-700 to-primary-800",
        modules: CONVIVENCIA_MODULES,
      };
  }
}